// Updated App.jsx - Fixed routing with Always-Active Homepage and PWA Install Prompt
import React from 'react';
import { motion } from 'framer-motion';
import { PrivacyAuthProvider, usePrivacyAuth } from './context/PrivacyAuthContext';
import { VibeGamificationProvider } from './context/VibeGamificationContext';
import { AppProvider, useApp } from './context/AppContext';
import { UserProvider } from './context/UserContext';
import { useContextIntegration } from './hooks/useContextIntegration';
import Navigation from './components/shared/Navigation';
import Footer from './components/shared/Footer';
import StandaloneSignup from './components/auth/StandaloneSignup';
// Lazy load pages for better performance
import VibeHomePage from './pages/VibeHomePage'; // Keep home page for immediate loading
const LazyUnifiedDashboard = React.lazy(() => import('./pages/UnifiedDashboard'));
const LazyVibeAssessmentIntro = React.lazy(() => import('./components/assessment/VibeAssessmentIntro'));
const LazyVibeAssessment = React.lazy(() => import('./components/assessment/VibeAssessment'));
const LazyVibeAssessmentResultsView = React.lazy(() => import('./components/assessment/VibeAssessmentResultsView'));
const LazyTeenModulesPage = React.lazy(() => import('./pages/TeenModulesPage'));
const LazyInnerVoiceModulesIntro = React.lazy(() => import('./components/innervoice/InnerVoiceModulesIntro'));
const LazyVibeToolsPage = React.lazy(() => import('./pages/VibeToolsPage'));
const LazyVibeResourcesPage = React.lazy(() => import('./pages/VibeResourcesPage'));
const LazyVibeDataPrivacyPage = React.lazy(() => import('./pages/VibeDataPrivacyPage'));
const LazyVibeLifeDesign = React.lazy(() => import('./pages/VibeLifeDesign'));
const LazyCoachingPage = React.lazy(() => import('./pages/CoachingPage'));
const LazyCoachingPrivacyPage = React.lazy(() => import('./pages/CoachingPrivacyPage'));
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ErrorBoundary from './components/errorBoundary/ErrorBoundary';
import errorTrackingService from './services/errorTracking/ErrorTrackingService';
import VibeLogin from './components/auth/VibeLogin';

// Local SkipLink component for accessibility
const SkipLink = ({ href = '#main-content' }) => (
  <a
    href={href}
    className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-purple-500 focus:text-white focus:rounded-lg"
  >
    Skip to main content
  </a>
);

// Loading fallback component for lazy loading
const LoadingFallback = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
      <div className="text-white text-lg font-semibold">Loading...</div>
      <div className="text-purple-300 text-sm mt-2">Getting things ready for you</div>
    </div>
  </div>
);

// Context Integration Component
const ContextIntegration = ({ children }) => {
  useContextIntegration(); // This connects AppContext with VibeGamificationContext
  
  return children;
};

const AppContent = () => {
  const { user, isLoading } = usePrivacyAuth();
  const { currentView, completeAssessment, setCurrentView } = useApp();
  
  // Views that don't require authentication (guest-friendly)
  const _guestAllowedViews = [
    'home', 
    'dashboard', // Now guest-friendly with limited features
    'vibe-assessment-intro',
    'vibe-assessment', 
    'resources',
    'data-privacy',
  ];

  // Views that require authentication - now only dashboard and data-privacy require auth
  const authRequiredViews = [
    'progress', // Legacy redirect to dashboard
    'life-design-hub', // New view placeholder
  ];

  const requiresAuth = authRequiredViews.includes(currentView);

  // Handle assessment completion and post-signup flow
  const handleAssessmentComplete = (results) => {
    completeAssessment(results);
    // After assessment, show results and then go to dashboard
    setCurrentView('dashboard');
  };

  // Handle auth-required navigation
  const handleAuthRequiredNavigation = (_view) => {
    if (!user && requiresAuth) {
      // Redirect to pure HTML signup page
      window.location.href = '/signup.html';
      return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white">Redirecting to signup...</div>
      </div>;
    }
    return null;
  };

  // Loading screen with Vibe styling
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"
          />
          <h2 className="text-2xl font-bold text-white mb-2">Loading Your Vibe...</h2>
          <p className="text-purple-200">Getting everything ready ✨</p>
        </div>
      </div>
    );
  }

  // Check if trying to access auth-required content as guest
  const authCheck = handleAuthRequiredNavigation(currentView);
  if (authCheck) return authCheck;
  
  
  const renderCurrentView = () => {
    // Handle all views with proper routing logic
    switch (currentView) {
      case 'dashboard':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyUnifiedDashboard />
          </React.Suspense>
        );
        
      case 'vibe-assessment-intro':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeAssessmentIntro onStart={() => setCurrentView('vibe-assessment')} />
          </React.Suspense>
        );
        
      case 'vibe-assessment':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeAssessment onComplete={handleAssessmentComplete} />
          </React.Suspense>
        );
      
      case 'vibe-assessment-results':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeAssessmentResultsView />
          </React.Suspense>
        );
        
      case 'modules-intro':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyInnerVoiceModulesIntro />
          </React.Suspense>
        );

      case 'modules':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyTeenModulesPage />
          </React.Suspense>
        );
        
      case 'progress':
        // Redirect legacy progress route to dashboard
        setCurrentView('dashboard');
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyUnifiedDashboard />
          </React.Suspense>
        );
        
      case 'tools':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeToolsPage />
          </React.Suspense>
        );

      case 'life-design':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeLifeDesign />
          </React.Suspense>
        );

      case 'life-design-landing':
        // For guests accessing life design
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeLifeDesign />
          </React.Suspense>
        );
        
      case 'data-privacy':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeDataPrivacyPage />
          </React.Suspense>
        );
        
      case 'coaching':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyCoachingPage />
          </React.Suspense>
        );
      case 'coaching-privacy':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyCoachingPrivacyPage />
          </React.Suspense>
        );
      case 'resources':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeResourcesPage />
          </React.Suspense>
        );
        
      case 'life-design-hub':
        // Placeholder for new Conscious Life Design modules
        return (
          <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-32">
            <div className="container mx-auto px-4 py-8 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-2xl mx-auto"
              >
                <div className="text-6xl mb-6">🔮</div>
                <h1 className="text-4xl font-bold text-white mb-4">
                  Conscious Life Design Hub
                </h1>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
                  <p className="text-xl text-purple-200 mb-6">
                    Your gateway to intentional life creation and future self work.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <h3 className="font-semibold text-white mb-2">Advanced Modules</h3>
                      <p className="text-sm text-slate-300">Life design, identity work, and future planning</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <h3 className="font-semibold text-white mb-2">Interactive Tools</h3>
                      <p className="text-sm text-slate-300">Vision boards, goal mapping, and reflection exercises</p>
                    </div>
                  </div>
                  <div className="px-4 py-2 bg-yellow-400 text-black rounded-full inline-block font-semibold">
                    🚧 Coming Soon - Under Development 🚧
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        );
        
      
      
      case 'home':
      default:
        // ALWAYS show the dynamic homepage - for both guests and authenticated users
        return <VibeHomePage />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SkipLink href="#main-content" />
      <Navigation />

      {/* Temporarily disable all AnimatePresence to isolate the useEffect issue */}
      <main id="main-content" tabIndex="-1" className="flex-1">
        {renderCurrentView()}
      </main>

      {/* Global Footer */}
      <Footer />

      {/* ADD PWA INSTALL PROMPT HERE - Shows globally across all views */}
      <PWAInstallPrompt />
    </div>
  );
};

// Create a simple context for navigation
const NavigationContext = React.createContext();

// Hook to trigger signup from navigation
window.triggerSignup = () => {
  window.location.href = `${window.location.origin }?signup=true`;
  window.location.reload();
};

// Top-level router before any context
const TopLevelRouter = () => {
  const [currentView, setCurrentView] = React.useState('home');
  
  React.useEffect(() => {
    // Check if we need to start in signup mode
    const urlParams = new URLSearchParams(window.location.search);
    console.log('🔍 TopLevelRouter: URL params:', window.location.search);
    console.log('🔍 TopLevelRouter: signup param:', urlParams.get('signup'));
    if (urlParams.get('signup') === 'true') {
      console.log('🎯 TopLevelRouter: Setting view to signup');
      setCurrentView('signup');
    }
  }, []);
  
  // Handle signup completely outside of any context
  if (currentView === 'signup') {
    console.log('🚀 TopLevelRouter: Rendering StandaloneSignup - NO CONTEXTS ACTIVE');
    return (
      <StandaloneSignup 
        onComplete={() => {
          // Navigate to dashboard after signup
          window.location.href = window.location.origin;
        }}
        onSkip={() => {
          // Navigate to home as guest
          window.location.href = window.location.origin;
        }}
        onBack={() => {
          // Navigate back to home
          window.location.href = window.location.origin;
        }}
      />
    );
  }
  
  // For all other views, use the full app with contexts
  return (
    <NavigationContext.Provider value={{ currentView, setCurrentView }}>
      <ErrorBoundary name="App">
        <PrivacyAuthProvider>
          <AppProvider>
            <AppRoutingWrapper />
          </AppProvider>
        </PrivacyAuthProvider>
      </ErrorBoundary>
    </NavigationContext.Provider>
  );
};

// Main App component with all providers
const App = () => {
  // Initialize error tracking
  React.useEffect(() => {
    // Set up error tracking context
    errorTrackingService.setContext({
      appVersion: '2.0.0',
      edition: 'vibe',
      environment: import.meta.env.MODE,
    });
    
    // Check if user logged in via HTML pages
    const authSuccess = localStorage.getItem('vibe_auth_success');
    const userId = localStorage.getItem('vibe_user_id');
    
    if (authSuccess === 'true' && userId) {
      console.log('User authenticated via HTML signup:', userId);
      // Clear the flags
      localStorage.removeItem('vibe_auth_success');
      
      // The PrivacyAuthContext should pick up the Firebase auth state
      // and update accordingly
    }
  }, []);

  return <TopLevelRouter />;
};

// Separate routing wrapper to handle signup isolation
const AppRoutingWrapper = () => {
  const { currentView, setCurrentView } = React.useContext(NavigationContext);
  // Override the app context currentView with our prop
  const appContext = useApp();
  React.useEffect(() => {
    appContext.setCurrentView(currentView);
  }, [currentView, appContext.setCurrentView]);
  
  
  // Handle login form isolated as well
  if (currentView === 'login') {
    return (
      <div className="min-h-screen">
        <Navigation />
        <VibeLogin 
          onComplete={() => setCurrentView('dashboard')} 
          onBackToSignup={() => setCurrentView('signup')} 
        />
        <PWAInstallPrompt />
      </div>
    );
  }
  
  // All other views get the full context
  return (
    <VibeGamificationProvider>
      <UserProvider>
        <ContextIntegration>
          <ErrorBoundary name="AppContent">
            <AppContent />
          </ErrorBoundary>
        </ContextIntegration>
      </UserProvider>
    </VibeGamificationProvider>
  );
};

export default App;